import { PageHero, Container } from "@/components/common/Section";
import { Reveal } from "@/lib/anim";

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    dateLabel: "Effective Date: 01-09-2026",
    numbered: true,
    intro:
      "At iJeeva (www.ijeeva.com), your privacy is very important to us. This Privacy Policy outlines how we collect, use, disclose, and safeguard your personal information when you visit our website or use our services. Please read this policy carefully to understand our views and practices regarding your personal data and how we treat it.",
    sections: [
      {
        h: "Information We Collect",
        p: "When you visit iJeeva or interact with our site, we may collect:",
        bullets: [
          "Personal Information such as your name, email address, phone number, or other identifiers when you fill out a form or register on the website.",
          "Usage Information such as your IP address, browser type, device information, and interactions with our website.",
          "Cookies and Tracking Technologies to enhance your experience and gather data about site traffic and interaction.",
        ],
      },
      {
        h: "When We Collect Information",
        p: "We collect information from you when:",
        bullets: [
          "You register on our site or subscribe to newsletters",
          "You fill out a form or contact us via the website",
          "You browse or use the website's features",
        ],
      },
      {
        h: "How We Use Your Information",
        p: "We use your information to:",
        bullets: [
          "Personalize your user experience",
          "Improve our website and service offerings",
          "Respond to customer service requests",
          "Send periodic emails related to our services",
          "Communicate regarding updates, offers, and events",
        ],
      },
      {
        h: "How We Protect Your Information",
        bullets: [
          "Information is stored on secure servers",
          "Access is restricted to authorized personnel only",
          "We use SSL (Secure Socket Layer) encryption where necessary",
          "Regular malware scanning is performed",
          "No sensitive financial data (like credit card numbers) is stored",
        ],
      },
      {
        h: "Cookies Policy",
        p: "We use cookies to:",
        bullets: [
          "Remember your preferences for future visits",
          "Understand and save user behavior for analytics",
          "Track advertising data via third-party partners like Google",
        ],
        p2: "You can choose to disable cookies through your browser settings. However, disabling cookies may affect the functionality of some features on the site.",
      },
      {
        h: "Third-Party Disclosure",
        p: "We do not sell or trade your personally identifiable information. We may share it with trusted third parties who help operate our website, as long as those parties agree to keep it confidential.",
      },
      {
        h: "Third-Party Links",
        p: "Our website may include links to third-party websites. These websites have separate and independent privacy policies. We are not responsible for the content or privacy practices of such sites.",
      },
      {
        h: "Google and Advertising",
        p: "We may use:",
        bullets: [
          "Google Analytics for tracking and analytics",
          "Google Ads and associated cookies for retargeting or interest-based advertising",
        ],
        p2: "You can opt out using the Google Ad Settings or by visiting the Network Advertising Initiative Opt Out page.",
      },
      {
        h: "Children's Privacy (COPPA)",
        p: "We do not knowingly collect data from children under the age of 13. If you believe we have such information, please contact us and we will promptly remove it.",
      },
      {
        h: "Your Data Rights",
        p: "You have the right to:",
        bullets: [
          "Request access to your personal data",
          "Request correction or deletion of your data",
          "Withdraw consent at any time",
          "Request to opt out of marketing communication",
        ],
        p2: "To exercise your rights, please email us at info@ijeeva.com",
      },
      {
        h: "Data Breach Notification",
        p: "If a data breach occurs, we will notify affected users via email within 7 business days.",
      },
      {
        h: "CAN-SPAM Compliance",
        p: "We comply with the CAN-SPAM Act and:",
        bullets: [
          "Never use false or misleading subjects or email addresses",
          "Identify messages as advertisements when applicable",
          "Include our business address in emails",
          "Provide clear opt-out/unsubscribe options",
        ],
        p2: "You may unsubscribe by clicking the link at the bottom of our emails or by contacting us directly.",
      },
      {
        h: "Contact Us",
        p: "If you have any questions or concerns about this Privacy Policy, please contact: iJeeva Software Solutions Pvt Ltd · Email: info@ijeeva.com · Website: https://www.ijeeva.com",
      },
    ],
  },
  terms: {
    title: "Terms & Conditions",
    dateLabel: "Last updated: 01-09-2026",
    numbered: false,
    intro:
      'These Terms and Conditions ("Terms"), together with our Privacy Policy and any other applicable terms, constitute a binding agreement between iJeeva Software Solutions Pvt. Ltd. ("iJeeva", "we", "us", or "our") and you ("you" or "your"). By accessing or using www.ijeeva.com ("Website") and the services offered on it ("Services"), you agree to be bound by these Terms. If you do not agree to these Terms, please do not use the Website or Services.',
    sections: [
      {
        h: "Modification of Terms",
        p: "We reserve the right to change or update these Terms at any time without prior notice. It is your responsibility to review these Terms periodically for updates. Continued use of the Website or Services constitutes your acceptance of any revised Terms.",
      },
      {
        h: "Eligibility and Account Responsibility",
        p: "To use our Services, you agree to provide accurate, current, and complete information during registration and thereafter. You are solely responsible for all activities that occur under your account.",
      },
      {
        h: "Use of Website and Services",
        p: "You agree to use the Website and Services only for lawful purposes. Unauthorized use of the Website or Services may result in legal action. We make no warranties or guarantees regarding the accuracy, performance, or suitability of the information provided on the Website.",
      },
      {
        h: "Intellectual Property",
        p: "All content, designs, and intellectual property on the Website belong to iJeeva Software Solutions Pvt. Ltd. You are not permitted to use or reproduce any materials without our prior written consent.",
      },
      {
        h: "Third-Party Links",
        p: "The Website may contain links to third-party websites. We are not responsible for the content or practices of these third-party sites. Your access to and use of such websites is at your own risk.",
      },
      {
        h: "Payment and Refunds",
        p: "By using our Services, you agree to pay all applicable fees. If we are unable to provide the Service, you may be eligible for a refund based on the specific terms related to that Service. Refund claims must be made within the stipulated time as outlined in our policy.",
      },
      {
        h: "Force Majeure",
        p: "iJeeva shall not be held liable for any failure or delay in performing obligations under these Terms due to causes beyond its reasonable control, including but not limited to natural disasters, strikes, internet outages, or government actions.",
      },
      {
        h: "Governing Law and Jurisdiction",
        p: "These Terms shall be governed by the laws of India. Any disputes arising under or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts in Bengaluru, Karnataka.",
      },
      {
        h: "Marketing Communication",
        p: "By using our Services or submitting your contact information, you consent to receive communications from us via email, phone, SMS, or other digital means. You can opt-out by following the unsubscribe instructions or contacting us directly.",
      },
      {
        h: "Contact Information",
        p: "For any questions or concerns regarding these Terms, please contact us via the information provided on our website: https://www.ijeeva.com",
      },
    ],
  },
};

export default function Legal({ kind }) {
  const c = CONTENT[kind] || CONTENT.privacy;
  return (
    <>
      <PageHero eyebrow="Legal" title={c.title} copy={c.intro} testid={`legal-${kind}-title`} />
      <section className="py-16 lg:py-20">
        <Container className="max-w-3xl">
          <Reveal className="mb-4">
            <span
              data-testid="legal-date-badge"
              className="inline-flex items-center rounded-full border border-[#E2E8F0] bg-white px-4 py-1.5 text-[13px] font-medium text-[#64748B]"
            >
              {c.dateLabel}
            </span>
          </Reveal>
          {c.sections.map((s, i) => (
            <Reveal key={s.h} delay={Math.min(i, 4) * 50} className="border-t border-[#E2E8F0] py-8 first:border-t-0">
              <h2 className="text-xl font-semibold text-[#1D305B]">
                {c.numbered ? `${i + 1}. ` : ""}
                {s.h}
              </h2>
              {s.p && <p className="mt-3 text-[15px] leading-relaxed text-[#64748B]">{s.p}</p>}
              {s.bullets && (
                <ul className="mt-3 space-y-2.5">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-[15px] leading-relaxed text-[#64748B]">
                      <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#0EA5A4]" aria-hidden="true" />
                      {b}
                    </li>
                  ))}
                </ul>
              )}
              {s.p2 && <p className="mt-3 text-[15px] leading-relaxed text-[#64748B]">{s.p2}</p>}
            </Reveal>
          ))}
          <p className="mt-6 text-sm text-[#94A3B8]">iJeeva Software Solutions Pvt. Ltd. · Bengaluru, India</p>
        </Container>
      </section>
    </>
  );
}
